import { useEffect, useRef } from 'react';
import { getAddress, isAddressEqual, type Address } from 'viem';

import { useAuthenticatedOwnerBinding } from '@/features/auth/authenticatedOwner';
import { useOAuthBindingDialogStore } from '@/features/auth/oauthBindingDialogStore';
// the store module only: the feature barrel also carries the flow's dialogs,
// and the sign-out one imports the logout routine.
import { useFlowStore } from '@/features/flow/store';
// the store module only, not the feature barrel: the barrel pulls the
// onboarding flow back into this module's own dependencies.
import { useOnboardingLauncher } from '@/features/onboarding/store';
import { useWallet } from '@/features/wallet/hooks/useWallet';

import { pairKey, useWalletSwitchStore } from './store';

/**
 * Watches for the connected wallet drifting away from the account the current
 * login belongs to, and raises the resolution prompt when it does.
 *
 * Only a switch counts. A wallet that was already pointing somewhere else when
 * the app loaded is a configuration, not an event: the social cohort funds
 * deposits from whatever account is connected, so a non-owner selection is a
 * supported setup that must not greet them with a modal on every page load.
 * The prompt is armed by the connected address CHANGING while the app runs,
 * which is also the only thing the copy ("a wallet switch was detected") claims.
 *
 * Inert by construction where there is nothing to compare: a social-only mobile
 * session has no connected wallet, and a signed-out visitor has no owner.
 *
 * Two flows deliberately ask the user to switch EOAs (onboarding, and the OAuth
 * account-binding dialog), so their prompt would be noise on top of an
 * instruction the app itself gave: while either is up, the guard stays silent.
 *
 * Inert while the browser wallet flow (V2) is in use: its account is the key
 * held in this browser, and a MetaMask account change means nothing to it.
 */
const WalletSwitchBridge = () => {
    const { address, isConnected } = useWallet();
    const binding = useAuthenticatedOwnerBinding();
    // read out of the object: it is rebuilt every render, so it can never be an
    // effect dependency.
    const owner = binding?.owner ?? null;
    const ownerSource = binding?.source ?? null;
    const onboardingOpen = useOnboardingLauncher((state) => state.dialogOpen);
    const bindingPending = useOAuthBindingDialogStore(
        (state) => state.pending !== null,
    );
    const browserWalletFlow = useFlowStore((state) => state.active === 'v2');
    const request = useWalletSwitchStore((state) => state.request);
    const clear = useWalletSwitchStore((state) => state.clear);

    // The last connected address this session saw. `null` until one arrives:
    // AppKit reconnects asynchronously, so every load starts undefined and the
    // address landing afterwards is an arrival, not a switch.
    const lastConnectedRef = useRef<Address | null>(null);
    // The address an observed in-session switch moved TO. The prompt is only
    // ever raised for this one, which keeps it armed across the re-runs this
    // effect does for unrelated reasons (a suppressing dialog opening and
    // closing, the owner being re-read) without re-arming it for an address
    // the user never switched to.
    const switchedToRef = useRef<Address | null>(null);

    useEffect(() => {
        let connected: Address | null = null;
        if (isConnected && address) {
            try {
                connected = getAddress(address);
            } catch {
                connected = null;
            }
        }
        // Bookkeeping first, and unconditionally: a switch performed while a
        // suppressing flow is open still happened, and has to be remembered so
        // the prompt can be raised once that flow closes. A disconnect leaves
        // the baseline alone on purpose, because wallets briefly report one
        // while switching accounts and resetting there would swallow the very
        // switch this watcher exists for.
        if (connected) {
            const previous = lastConnectedRef.current;
            lastConnectedRef.current = connected;
            if (previous && previous !== connected) {
                switchedToRef.current = connected;
            }
        }

        if (
            !connected ||
            !owner ||
            !ownerSource ||
            onboardingOpen ||
            bindingPending ||
            browserWalletFlow
        ) {
            clear();
            return;
        }
        if (isAddressEqual(connected, owner)) {
            // switched back on their own: the question answered itself.
            clear();
            return;
        }
        if (switchedToRef.current !== connected) {
            // a pre-existing selection, not a switch: stay silent.
            clear();
            return;
        }
        const pair = {
            currentOwner: owner,
            nextAddress: connected,
            ownerSource,
        };
        // re-reading rather than subscribing: a dismissal must not feed itself
        // back into this effect, it only ever suppresses the next evaluation.
        if (pairKey(pair) === useWalletSwitchStore.getState().dismissedKey) {
            // still a clear: a prompt raised for some third account has to come
            // down, or it would sit there offering to sign the user out into an
            // address the wallet already moved off.
            clear();
            return;
        }
        request(pair);
    }, [
        address,
        bindingPending,
        browserWalletFlow,
        clear,
        isConnected,
        onboardingOpen,
        owner,
        ownerSource,
        request,
    ]);

    return null;
};

export default WalletSwitchBridge;
